/**
 * Authentication client
 * Handles login and token management
 */

import type { LoginCredentials, LoginResponse } from "./types.js";

/**
 * Authentication client for managing JWT tokens
 */
export class AuthClient {
  private token: string | null = null;
  private makeRequest: (path: string, options: any) => Promise<any>;

  constructor(makeRequest: (path: string, options: any) => Promise<any>) {
    this.makeRequest = makeRequest;
  }

  /**
   * Login with email and password
   *
   * @param credentials - Email and password
   * @returns JWT token and user information
   *
   * @example
   * const { token, user } = await client.auth.login({
   *   email: 'admin@example.com',
   *   password: 'password123'
   * });
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await this.makeRequest("/api/v1/auth/login", {
      method: "POST",
      body: credentials,
      skipAuth: true, // Login doesn't require HMAC auth
    });

    // Store token automatically
    this.token = response.token;

    return response;
  }

  /**
   * Set authentication token manually
   *
   * @param token - JWT token
   *
   * @example
   * client.auth.setToken('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...');
   */
  setToken(token: string): void {
    this.token = token;
  }

  /**
   * Get current authentication token
   *
   * @returns Current JWT token or null
   *
   * @example
   * const token = client.auth.getToken();
   * if (token) {
   *   // Store in localStorage, cookie, etc.
   *   localStorage.setItem('auth_token', token);
   * }
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Clear authentication token
   *
   * @example
   * client.auth.clearToken();
   */
  clearToken(): void {
    this.token = null;
  }

  /**
   * Check if user is authenticated
   *
   * @returns True if token is set
   */
  isAuthenticated(): boolean {
    return this.token !== null;
  }
}
